import { useSelector } from "react-redux";
import * as _ from "lodash";
import { Options } from "@ant-design/charts";

import { DataDescriptorEntity, EnterpriseAppState, Enterprise as API } from "@ctra/api";
import { Nullable, Optional, UnitSystem, Units } from "@ctra/utils";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";

import { useLocalization } from "@base";

/**
 * Make a chart annotation for displaying goal values
 * @param {<number>} value
 * @param {string} label
 * @param {<DataDescriptorEntity["id"]>} dataDescriptorID
 * @return {Options["annotations"]}
 */
export const useTargetAnnotation = (
  value: Optional<number>,
  label: string,
  dataDescriptorID?: Nullable<DataDescriptorEntity["id"]>
): Options["annotations"] => {
  const { t } = useTranslation();
  const { unitSystem } = useLocalization();

  const {
    unitSystem: { unit: tUnit }
  } = Content;

  const dataDescriptor = useSelector<EnterpriseAppState, Nullable<DataDescriptorEntity>>((state) =>
    dataDescriptorID ? API.entities.getDataDescriptor(state, { id: dataDescriptorID }) : null
  );

  const units = dataDescriptor?.valueProperties.units;
  const unit = _.get(units, unitSystem, _.get(units, UnitSystem.metric));

  /**
   * Format the value if the data descriptor is given
   * @type {string | number | undefined}
   */
  const formattedValue =
    dataDescriptor && unit ? Units.format(value, unit, t<string>(tUnit(unit)), { precision: 1 }) : value;

  const color = "#237804";
  const paddingH = 10;

  return value
    ? [
        {
          type: "line",
          start: ["start", value],
          end: ["end", value],
          style: {
            stroke: color,
            lineDash: [9, 3],
            lineWidth: 1
          }
        },
        {
          type: "text",
          position: ["end", value],
          content: `${label ? `${label} - ` : ""}${formattedValue}`,
          style: {
            fill: color,
            fontSize: 13,
            textAlign: "right"
          },
          offsetX: -paddingH,
          background: {
            style: {
              fill: "#ffffff"
            },
            padding: [7, paddingH]
          }
        }
      ]
    : [];
};
